import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSavedJobs } from '../services/savedJobsService';
import { applyForJob } from '../services/applicationService';
import { getJobById } from '../services/jobService';
import JobCard from '../components/JobCard';
import JobApplicationModal from '../components/JobApplicationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

import ContentWithAds from '../components/ContentWithAds';

const SavedJobs = ({ user, userData }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const fetchSavedJobs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get saved job records
      const savedJobsResult = await getSavedJobs(user.uid);

      if (!savedJobsResult.success) {
        setError(savedJobsResult.error);
        return;
      }

      // Fetch actual job details for each saved job
      const jobPromises = savedJobsResult.savedJobs.map(async (savedJob) => {
        try {
          const jobResult = await getJobById(savedJob.jobId);
          if (jobResult.success) {
            return {
              ...jobResult.job,
              savedAt: savedJob.savedAt,
              savedJobId: savedJob.id
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching job ${savedJob.jobId}:`, error);
          return null;
        }
      });

      const jobDetails = await Promise.all(jobPromises);
      const validJobs = jobDetails.filter(job => job !== null);

      setJobs(validJobs);
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
      setError('Failed to load saved jobs');
    } finally {
      setLoading(false);
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userData?.userType !== 'jobseeker') {
      navigate('/dashboard');
      return;
    }

    fetchSavedJobs();
  }, [user, userData, navigate, fetchSavedJobs]);

  const handleApplyClick = (job, setJobApplying, setJobApplied) => {
    setSelectedJob(job);
    setShowJobApplicationModal(true);
  };

  const handleConfirmApplication = async (applicationData) => {
    if (!selectedJob) return;

    setApplying(true);
    try {
      const result = await applyForJob(selectedJob.id, user.uid, applicationData);

      if (result.success) {
        setShowJobApplicationModal(false);
        setSelectedJob(null);
        setShowSuccessAnimation(true);

        // Hide success animation after 3 seconds
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleJobUnsaved = (jobId) => {
    // Remove the job from the local state when it's unsaved
    setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pb-24 lg:pb-0">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-72 h-72 bg-white opacity-10 rounded-full -translate-x-36 -translate-y-36"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-48 -translate-y-48"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Saved Jobs
                </h1>
              </div>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Your collection of interesting opportunities
              </p>
              <div className="flex items-center justify-center gap-6 text-white">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                  <span className="text-lg font-semibold">{jobs.length} saved jobs</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ContentWithAds>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {error && (
              <div className="mb-8 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {jobs.length === 0 && !error ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">No saved jobs yet</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Start building your collection by saving jobs that interest you. Click the heart icon on any job card to save it here.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/jobs')}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Browse Jobs
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-8 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl border border-gray-200"
                  >
                    Back to Dashboard
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {/* Filter and Sort Options */}
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Your Saved Jobs</h2>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">
                        {jobs.length} job{jobs.length !== 1 ? 's' : ''} saved
                      </span>
                      <button
                        onClick={fetchSavedJobs}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>

                {/* Jobs Grid */}
                <div className="space-y-6">
                  {jobs.map((job) => (
                    <div key={job.id} className="relative">
                      <div className="absolute top-4 right-4 z-10">
                        <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium border border-purple-200">
                          Saved {job.savedAt ? new Date(job.savedAt).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <JobCard
                        job={job}
                        showActions={false}
                        user={user}
                        userData={userData}
                        onJobUnsaved={() => handleJobUnsaved(job.id)}
                        onApplyClick={handleApplyClick}
                      />
                    </div>
                  ))}
                </div>

                {/* Load More or Pagination could go here */}
                {jobs.length > 0 && (
                  <div className="text-center mt-12">
                    <button
                      onClick={() => navigate('/jobs')}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      Find More Jobs
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </ContentWithAds>
      </div>

      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-300 scale-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Application Submitted!</h3>
            <p className="text-gray-600 mb-4">Your application has been sent successfully. Good luck!</p>
            <div className="flex items-center justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Modal */}
      <JobApplicationModal
        job={selectedJob}
        isOpen={showJobApplicationModal}
        onClose={() => {
          setShowJobApplicationModal(false);
          setSelectedJob(null);
        }}
        onConfirm={handleConfirmApplication}
        applying={applying}
      />

      {/* Mobile Bottom Navigation */}


      {/* Footer */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default SavedJobs;
